export type Coordinate2D = [number, number];

// =============================================================================
// BOUNDING BOX
// =============================================================================

/**
 * Bounding box configuration with named properties.
 * All values are in user coordinates.
 */
export interface BoundingBoxConfig {
  /** Left edge of the visible area (minimum x) */
  xStart: number;
  /** Right edge of the visible area (maximum x) */
  xEnd: number;
  /** Bottom edge of the visible area (minimum y) */
  yStart: number;
  /** Top edge of the visible area (maximum y) */
  yEnd: number;
}

/** @internal JSXGraph native bounding box format: [left, top, right, bottom] */
export type BoundingBoxTuple = [number, number, number, number];

/**
 * Convert BoundingBoxConfig to JSXGraph format [left, top, right, bottom].
 * @internal Used internally to communicate with JSXGraph API.
 */
export function boundingBoxToTuple(bbox: BoundingBoxConfig): BoundingBoxTuple {
  return [bbox.xStart, bbox.yEnd, bbox.xEnd, bbox.yStart];
}

// =============================================================================
// GRID
// =============================================================================

export type GridType = 'lines' | 'dots';

export interface GridConfig {
  /** Grid type: 'lines' extends from axis ticks, 'dots' renders points at intersections */
  type?: GridType;
  /** Whether the grid is visible. @default true */
  visible?: boolean;
}

// =============================================================================
// TICKS
// =============================================================================

/**
 * Tick label format presets.
 * - 'pi': Labels as multiples of π (0, π/2, π, 3π/2, 2π, ...)
 * - 'degrees': Labels as degrees (0°, 90°, 180°, ...)
 * - 'default': Standard numeric labels
 */
export type TickLabelFormat = 'pi' | 'degrees' | 'default';

export interface TickLabelConfig {
  /** Font size in pixels */
  fontSize?: number;
  /** Label offset from tick [x, y] in pixels */
  offset?: [number, number];
}

export interface TicksConfig {
  /** Distance between major ticks in user coordinates */
  ticksDistance?: number;
  /** Preset label format for common use cases (pi, degrees, default) */
  labelFormat?: TickLabelFormat;
  /** Number of minor ticks between two major ticks */
  minorTicks?: number;
  /** Height of major ticks in pixels. Use -1 to extend to grid lines */
  majorHeight?: number;
  /** Height of minor ticks in pixels */
  minorHeight?: number;
  /** Show labels on ticks */
  drawLabels?: boolean;
  /** Show label at origin (zero) */
  drawZero?: boolean;
  /** Scale factor for tick label values */
  scale?: number;
  /** Symbol to append to labels (e.g., "π") */
  scaleSymbol?: string;
  /** Custom array of tick positions (overrides ticksDistance) */
  fixedTicks?: number[];
  /** Custom array of tick labels (must match fixedTicks length) */
  labels?: string[];
  /** Label styling options */
  label?: TickLabelConfig;
}

// =============================================================================
// AXIS LABEL
// =============================================================================

export interface AxisLabelConfig {
  /** Label text (e.g., "x", "y", "θ") */
  text: string;
  /** Offset from axis endpoint [x, y] in pixels */
  offset?: [number, number];
  /** Font size in pixels */
  fontSize?: number;
}

// =============================================================================
// AXIS
// =============================================================================

/** Axis orientation */
export type AxisOrientation = 'horizontal' | 'vertical';

export interface AxisConfig {
  /** Unique identifier for the axis */
  id: string;
  /** Axis orientation: 'horizontal' (x-like) or 'vertical' (y-like) */
  orientation: AxisOrientation;
  /**
   * Position of the axis in user coordinates.
   * - For horizontal axes: y-coordinate where the axis is placed
   * - For vertical axes: x-coordinate where the axis is placed
   * - Use 0 for axes through the origin
   * @default 0
   */
  position?: number;
  /**
   * Whether this is a primary axis.
   * Primary axes control grid line generation when grid type is 'lines'.
   * Typically, you have one primary horizontal and one primary vertical axis.
   * @default true for the first axis of each orientation, false otherwise
   */
  isPrimary?: boolean;
  /**
   * Whether grid lines should extend from this axis's ticks.
   * Only applies to primary axes when grid type is 'lines'.
   * @default true for primary axes
   */
  drawGridLines?: boolean;
  /** Tick configuration for this axis */
  ticks?: TicksConfig;
  /** Axis label configuration */
  axisLabel?: AxisLabelConfig;
}

// =============================================================================
// COORDINATE SYSTEM
// =============================================================================

export interface CoordinateSystemConfig {
  /**
   * Bounding box defining the visible area.
   * Specify as: { xStart, xEnd, yStart, yEnd }
   */
  boundingbox?: BoundingBoxConfig;
  /**
   * Grid configuration.
   * - `true`: Enable default grid (lines type)
   * - `false`: Disable grid
   * - `GridConfig`: Custom grid configuration
   *
   * Note: For 'lines' grid, spacing is derived from primary axis tick distances.
   * For 'dots' grid, dots are placed at intersections of primary axis ticks.
   */
  grid?: GridConfig | boolean;
  /**
   * Axis configurations.
   * - `true`: Enable default axes (x and y through origin)
   * - `false`: Disable all axes
   * - `AxisConfig[]`: Custom array of axis configurations
   *
   * Each axis can be positioned independently with its own tick configuration.
   *
   * @example Two x-axes (radians and degrees)
   * ```ts
   * axes: [
   *   { id: 'x-rad', orientation: 'horizontal', position: 0, ticks: { labelFormat: 'pi' } },
   *   { id: 'x-deg', orientation: 'horizontal', position: -2, ticks: { labelFormat: 'degrees' } },
   *   { id: 'y', orientation: 'vertical', position: 0 },
   * ]
   * ```
   */
  axes?: AxisConfig[] | boolean;
}
